import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { ArrowLeft, Check } from 'lucide-react'
import { useUserStore } from '../../stores'
import { useI18n } from '../../i18n'
import { isSmsRefused, smsRefusedSentence } from '../../utils/smsRefusal'
import { ResponsiveWrapper } from '../../components/layout'
import BottomActionBar from '../../components/BottomActionBar'

// v242.6.0 (C/#11): sequential verification. New (deferred) signups verify the
// phone code, then the email code, before the account is created. The legacy
// sign-in "needs verification" path (an existing unverified row, no pending
// token) still does a single phone step.
function CodeField({ value, onChange, inputRef }) {
  const digits = value.split('')
  return (
    <div className="relative mb-4">
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={value}
        onChange={onChange}
        maxLength={6}
        className="absolute inset-0 w-full h-full opacity-0 z-10 cursor-pointer"
      />
      <div className="flex gap-2">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={`w-12 h-14 flex items-center justify-center text-2xl font-semibold border-2 rounded-xl transition-colors ${
              i === value.length
                ? 'border-orella-500 bg-orella-50'
                : digits[i]
                  ? 'border-orella-300 bg-white'
                  : 'border-gray-200 bg-white'
            }`}
          >
            {digits[i] || ''}
          </div>
        ))}
      </div>
    </div>
  )
}

export default function Verify() {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    verify, verifyPhone, verifyEmail, resendCode,
    pendingToken, pendingPhone, pendingEmail,
    loading, error: storeError,
  } = useUserStore()
  const { t } = useI18n()

  const deferred = !!pendingToken
  const [step, setStep] = useState('phone') // 'phone' | 'email'
  const [phoneCode, setPhoneCode] = useState('')
  const [emailCode, setEmailCode] = useState('')
  const [error, setError] = useState('')
  // TF-13 (v242.61.0): the server tells us whether the email code actually went
  // out. Nothing used to read it — see handleVerify.
  const [emailSendFailed, setEmailSendFailed] = useState(false)
  const [resendTimer, setResendTimer] = useState(30)
  const [verifying, setVerifying] = useState(false)
  const phoneRef = useRef(null)
  const emailRef = useRef(null)

  // SIGNUP-TESTCODE-DEAD-1 (v242.61.0): `testCode` is gone. Three places read it
  // as an SMS-failure fallback and THE SERVER HAS NEVER SENT THAT FIELD --
  // `grep -c testCode api/index.py` is 0. It rendered an amber "Test Code (SMS
  // pending)" panel that could not populate under any condition: a safety net
  // wired to nothing, which is the same shape as the onError avatar in
  // MDPHOTO-STORAGE-1. The i18n key `auth.testCodeLabel` is left in the locale
  // files; nothing references it.
  const smsSent = location.state?.smsSent ?? true

  useEffect(() => {
    phoneRef.current?.focus()
    const timer = setInterval(() => setResendTimer((p) => (p > 0 ? p - 1 : 0)), 1000)
    return () => clearInterval(timer)
  }, [])

  const code = step === 'phone' ? phoneCode : emailCode
  const setActiveCode = (v) => {
    if (step === 'phone') setPhoneCode(v)
    else setEmailCode(v)
    setError('')
  }
  const handleChange = (e) => setActiveCode(e.target.value.replace(/\D/g, '').slice(0, 6))

  const goOnboarding = () => {
    try { sessionStorage.removeItem('signup_form_draft') } catch {}
    navigate('/onboarding', { replace: true })
  }

  const handleVerify = async () => {
    if (code.length !== 6) { setError(t('auth.enterFullCode')); return }
    setVerifying(true); setError('')

    if (step === 'phone') {
      if (deferred) {
        const result = await verifyPhone(code)
        setVerifying(false)
        if (result.success) {
          // TF-13 (v242.61.0) — THE FIX. This used to advance to "enter the code we
          // emailed you" unconditionally, including when the server had just told
          // us `emailSent: false`. The patient was then parked forever on a screen
          // waiting for mail that was never sent. That is the TestFlight report
          // word for word: "I don't receive an email to verify my account, so I'm
          // stuck."
          //
          // We still ADVANCE -- the phone code is spent and sending them back would
          // burn a second Twilio verification -- but we say so plainly and open the
          // resend immediately instead of making them wait out the 30s timer.
          const failed = result.emailSent === false
          setEmailSendFailed(failed)
          setStep('email'); setResendTimer(failed ? 0 : 30)
          if (failed) {
            setError(t('auth.emailSendFailed', {
              defaultValue: "We couldn't send the email just now. Tap Resend code below.",
            }))
          }
          setTimeout(() => emailRef.current?.focus(), 550)
        } else {
          setError(result.error || t('auth.invalidCode'))
        }
      } else {
        const result = await verify(code)
        if (result.success) goOnboarding()
        else { setError(result.error || t('auth.invalidCode')); setVerifying(false) }
      }
    } else {
      const result = await verifyEmail(code)
      if (result.success) goOnboarding()
      else { setError(result.error || t('auth.invalidCode')); setVerifying(false) }
    }
  }

  const handleResend = async () => {
    if (resendTimer > 0) return
    // TF-13: resend-code reports `success` from the actual send, so a repeat
    // failure stays visible rather than silently resetting the timer.
    const r = await resendCode(step)
    // PROD-807: a refusal sent nothing - say why, start no cooldown, keep the entered code.
    if (isSmsRefused(r)) {
      setError(smsRefusedSentence(r) || t('auth.invalidCode'))
      return
    }
    if (step === 'email') {
      const ok = r?.success !== false
      setEmailSendFailed(!ok)
      setError(ok ? '' : t('auth.emailSendFailed', {
        defaultValue: "We couldn't send the email just now. Tap Resend code below.",
      }))
    }
    setResendTimer(30)
    setActiveCode('')
    ;(step === 'phone' ? phoneRef : emailRef).current?.focus()
  }

  const onBack = () => {
    if (step === 'email') { setStep('phone'); setResendTimer(0); return }
    if (location.state?.from === 'signin') navigate(-1)
    else navigate('/signup')
  }

  return (
    <ResponsiveWrapper className="bg-white" bottomInset={false}>
    <div className="screen-min-h bg-white px-6 pt-6 md:px-10 md:pt-10 flex flex-col">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="p-2 -ml-2 text-gray-600">
          <ArrowLeft size={24} />
        </button>
        {deferred && (
          <div className="flex items-center gap-1.5">
            <span className={`h-1.5 rounded-full transition-all duration-300 ${step === 'phone' ? 'w-5 bg-orella-500' : 'w-1.5 bg-emerald-400'}`} />
            <span className={`h-1.5 rounded-full transition-all duration-300 ${step === 'email' ? 'w-5 bg-orella-500' : 'w-1.5 bg-gray-200'}`} />
          </div>
        )}
      </div>

      <div className="flex-1 pt-8">
        <div className="overflow-hidden">
          <div
            className="flex w-[200%] transition-transform duration-500"
            style={{ transform: step === 'email' ? 'translateX(-50%)' : 'translateX(0)', transitionTimingFunction: 'cubic-bezier(.4,0,.2,1)' }}
          >
            {/* Phone step */}
            <div className="w-1/2 flex-shrink-0 flex flex-col items-center px-1">
              <h1 className="text-2xl md:text-3xl font-bold text-orella-700 mb-2">{t('auth.verifyPhone')}</h1>
              <p className="text-gray-500 text-center mb-4 md:text-lg">
                {smsSent
                  ? `${t('auth.codeSentTo')} ${pendingPhone || t('auth.phone')}. ${t('auth.enterCodeBelow')}`
                  : t('auth.smsPending')}
              </p>
              <CodeField value={phoneCode} onChange={step === 'phone' ? handleChange : () => {}} inputRef={phoneRef} />
            </div>

            {/* Email step */}
            <div className="w-1/2 flex-shrink-0 flex flex-col items-center px-1">
              <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-full px-3 py-1 mb-4">
                <Check size={14} className="text-emerald-600" />
                <span className="text-xs font-bold text-emerald-700">{t('auth.phoneVerified', { defaultValue: 'Phone verified' })}</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-orella-700 mb-2">{t('auth.verifyEmail', { defaultValue: 'Verify your email' })}</h1>
              {/* TF-13: do not claim we emailed them if the send failed. */}
              <p className="text-gray-500 text-center mb-4 md:text-lg">
                {emailSendFailed
                  ? t('auth.emailNotSent', {
                      defaultValue: "We couldn't send your email code. Tap Resend code below to try again.",
                    })
                  : `${t('auth.codeSentTo')} ${pendingEmail || t('auth.email', { defaultValue: 'your email' })}. ${t('auth.enterCodeBelow')}`}
              </p>
              <CodeField value={emailCode} onChange={step === 'email' ? handleChange : () => {}} inputRef={emailRef} />
            </div>
          </div>
        </div>

      </div>

      {/* PROD-1194: Verify / Continue / Create account is pinned at the bottom (resend under it). */}
      <BottomActionBar className="-mx-6 md:-mx-10 [--bar-px:24px] flex flex-col items-center"
        note={(error || storeError) ? <p className="text-red-500 text-sm mb-3 text-center">{error || storeError}</p> : null}>
          <button
            onClick={handleVerify}
            disabled={verifying || loading || code.length !== 6}
            className="w-full btn-primary mb-3 disabled:opacity-50 breathe-blue"
          >
            {verifying
              ? t('auth.verifying')
              : (deferred && step === 'phone')
                ? t('auth.continue', { defaultValue: 'Continue' })
                : (step === 'email')
                  ? t('auth.createAccount', { defaultValue: 'Create account' })
                  : t('auth.verify')}
          </button>
          <button
            onClick={handleResend}
            disabled={resendTimer > 0 || loading}
            className={`text-sm ${resendTimer > 0 ? 'text-gray-400' : 'text-orella-500'}`}
          >
            {resendTimer > 0 ? `${t('auth.resendIn')} ${resendTimer}s` : t('auth.resendCode')}
          </button>
      </BottomActionBar>
    </div>
    </ResponsiveWrapper>
  )
}
